import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { AddToCartAnimationStartEvent, CartAddEvent } from '@theme/events';
import { isMobileBreakpoint, onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  /** @type {Promise<void> | null} */
  #latestAddToCartAnimation = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(AddToCartAnimationStartEvent.eventName, this.#handleAddToCartAnimationStart);
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(AddToCartAnimationStartEvent.eventName, this.#handleAddToCartAnimationStart);
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.#historyAbortController?.abort();
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  /**
   * Tracks the current add-to-cart animation so auto-open can wait for it.
   * @param {CustomEvent<{ animation?: Promise<void> }>} event
   */
  #handleAddToCartAnimationStart = (event) => {
    if (!event.detail.animation) return;

    const animation = event.detail.animation.catch(() => {});
    this.#latestAddToCartAnimation = animation;

    animation.finally(() => {
      if (this.#latestAddToCartAnimation === animation) {
        this.#latestAddToCartAnimation = null;
      }
    });
  };

  /**
   * Waits for any active add-to-cart/cart-icon animations that are visible when the cart update lands.
   */
  #waitForAddToCartAnimations = async () => {
    await this.#latestAddToCartAnimation;

    await new Promise((resolve) => requestAnimationFrame(resolve));

    const animationElements = Array.from(
      document.querySelectorAll('fly-to-cart, .add-to-cart-button[data-added="true"], .cart-bubble--animating')
    );
    if (!animationElements.length) return;

    await onAnimationEnd(animationElements);
  };

  /**
   * Handles cart add events - opens drawer after add-to-cart animations and announces count when open.
   * @param {CustomEvent<{ resource?: { item_count?: number }, data?: { source?: string, didError?: boolean } }>} event
   */
  #handleCartAdd = async (event) => {
    const isProductFormAdd = event.detail.data?.source === 'product-form-component' && !event.detail.data?.didError;

    if (this.hasAttribute('auto-open') || isProductFormAdd) {
      await this.#waitForAddToCartAnimations();
      this.showDialog();
    }

    this.#announceCartCount(event.detail.resource?.item_count);
  };

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
